/**
 * The two facts about the FranchiseSign app that live outside the tvOS repo.
 *
 * Both are deliberately isolated here so supplying one is a single-line change
 * in one file, rather than an edit inside page markup in two applications.
 * Everything else on the setup page is read from the shipping tvOS project and
 * verified: the app is `com.franchisesign.tv`, tvOS only, and talks to
 * https://apple-tv.prospera.ai.
 */

/**
 * The App Store listing, once there is one.
 *
 * NULL until a real App Store ID is supplied from App Store Connect — the player
 * repo contains none, and a dead or wrong store link is worse than no link. The
 * button is simply not rendered while this is null; the search instructions are
 * the primary route regardless, because **a web link cannot be opened on an
 * Apple TV at all**. That is also why the label is "View on the App Store" and
 * never "Download now".
 *
 * The supplied link was https://apps.apple.com/pk/app/franchisesign/id6803275198.
 * The `/pk/` storefront segment is dropped deliberately: without it Apple sends
 * each visitor to their OWN country's store, which is what a link shown to every
 * account needs to do.
 */
export const APP_STORE_URL: string | null = 'https://apps.apple.com/app/id6803275198';

/**
 * A photograph of the app's pairing screen, so the user recognises what they are
 * looking at on the wall.
 *
 * NULL until a real capture is supplied. While it is null both applications draw
 * a simple representation of the same layout instead — QR block, large code,
 * three steps — which is honest about being a diagram and cannot go stale. A
 * grey placeholder box would tell the user nothing.
 *
 * To enable: drop the file in `public/apple-tv/` and set the path here, e.g.
 * `/apple-tv/pairing-screen-prosperasign.png`. Use a dummy code in the capture.
 */
export const PAIRING_SCREENSHOT_URL: string | null = '/apple-tv/pairing-screen-prosperasign.png';

/** Where the five steps hand off to. */
export const APPLE_TV_ROUTES = {
  locations: '/marketing/apple-tv/locations',
  media: '/marketing/apple-tv/media',
  playlists: '/marketing/apple-tv/playlists',
  screens: '/marketing/apple-tv/screens',
  getStarted: '/marketing/apple-tv/get-started',
} as const;
